/**
 * custom-homepage.js
 * Navbar scroll · Mobile menu · Reviews carousel · Quick-add cart · Scroll reveal · Parallax
 */
(function () {
  'use strict';

  const qs  = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ── 1. NAVBAR ─────────────────────────────────────────── */
  const navbar     = qs('#chp-navbar');
  const burger     = qs('#chp-burger');
  const mobileMenu = qs('#chp-mobile-menu');

  if (navbar) {
    const onScroll = () => navbar.classList.toggle('is-scrolled', window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  if (burger && mobileMenu) {
    burger.addEventListener('click', () => {
      const isOpen = mobileMenu.classList.toggle('is-open');
      burger.classList.toggle('is-active', isOpen);
      burger.setAttribute('aria-expanded', isOpen);
      mobileMenu.setAttribute('aria-hidden', !isOpen);
    });
    document.addEventListener('click', (e) => {
      if (!navbar.contains(e.target) && mobileMenu.classList.contains('is-open')) {
        mobileMenu.classList.remove('is-open');
        burger.classList.remove('is-active');
        burger.setAttribute('aria-expanded', 'false');
        mobileMenu.setAttribute('aria-hidden', 'true');
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
        mobileMenu.classList.remove('is-open');
        burger.classList.remove('is-active');
        burger.setAttribute('aria-expanded', 'false');
        burger.focus();
      }
    });
  }

  /* ── 2. REVIEWS CAROUSEL ───────────────────────────────── */
  const reviewsWrap  = qs('#chp-reviews-track-wrap');
  const reviewsTrack = qs('#chp-reviews-track');
  const prevBtn      = qs('#chp-reviews-prev');
  const nextBtn      = qs('#chp-reviews-next');
  const dotsWrap     = qs('#chp-reviews-dots');

  if (reviewsTrack && prevBtn && nextBtn) {
    const cards = qsa('.chp-review-card', reviewsTrack);
    let currentPage = 0;
    let perPage = getPerPage();

    function getPerPage() {
      if (window.innerWidth <= 768)  return 1;
      if (window.innerWidth <= 1024) return 2;
      return 3;
    }
    function totalPages() { return Math.ceil(cards.length / perPage); }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      for (let i = 0; i < totalPages(); i++) {
        const dot = document.createElement('button');
        dot.className = 'chp-reviews__dot' + (i === currentPage ? ' is-active' : '');
        dot.setAttribute('aria-label', `Go to review page ${i + 1}`);
        dot.addEventListener('click', () => goTo(i));
        dotsWrap.appendChild(dot);
      }
    }

    function updateDots() {
      if (!dotsWrap) return;
      qsa('.chp-reviews__dot', dotsWrap).forEach((d, i) => d.classList.toggle('is-active', i === currentPage));
    }

    function goTo(page) {
      currentPage = Math.max(0, Math.min(page, totalPages() - 1));
      const gap    = 24;
      const cardEl = cards[0];
      if (cardEl && reviewsWrap) {
        const cardW  = reviewsWrap.offsetWidth / perPage - gap * (perPage - 1) / perPage;
        const offset = currentPage * (cardW + gap);
        reviewsTrack.style.transform = `translateX(-${offset}px)`;
      }
      updateDots();
      prevBtn.disabled = currentPage === 0;
      nextBtn.disabled = currentPage >= totalPages() - 1;
    }

    prevBtn.addEventListener('click', () => goTo(currentPage - 1));
    nextBtn.addEventListener('click', () => goTo(currentPage + 1));

    // Swipe support
    let touchStartX = 0;
    reviewsTrack.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    reviewsTrack.addEventListener('touchend',   (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 50) goTo(dx < 0 ? currentPage + 1 : currentPage - 1);
    }, { passive: true });

    // Rebuild on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { perPage = getPerPage(); currentPage = 0; buildDots(); goTo(0); }, 200);
    });

    buildDots();
    goTo(0);
  }

  /* ── 3. QUICK ADD TO CART ──────────────────────────────── */
  const toast    = qs('#chp-toast');
  const toastMsg = qs('#chp-toast-msg');
  let toastTimer;

  function showToast(msg) {
    if (!toast) return;
    toastMsg.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3000);
  }

  async function addToCart(variantId, btn) {
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      const res  = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      const data = await res.json();
      showToast(`✓ ${data.title || 'Item'} added to cart!`);
      updateCartCount();
    } catch (err) {
      showToast('Something went wrong. Please try again.');
      console.error('[chp] cart error:', err);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
    }
  }

  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      const selectors = ['.cart-count','.header__cart-count','[data-cart-count]','.cart__count','#CartCount','.js-cart-count'];
      selectors.forEach(sel => qsa(sel).forEach(el => { el.textContent = cart.item_count; el.setAttribute('data-cart-count', cart.item_count); }));
    } catch (e) { /* non-critical */ }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.chp-product-card__quick-add');
    if (!btn) return;
    e.preventDefault();
    addToCart(btn.dataset.variantId, btn);
  });

  /* ── 4. SCROLL REVEAL ──────────────────────────────────── */
  if ('IntersectionObserver' in window) {
    requestAnimationFrame(() => {
      qsa('.chp-section-header, .chp-how__step, .chp-cta__content').forEach(el => el.classList.add('chp-reveal'));
      qsa('.chp-product-grid, .chp-trust-bar__list, .chp-how__steps').forEach(el => el.classList.add('chp-stagger'));
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); } });
      }, { threshold: 0.12, rootMargin: '0px 0px -48px 0px' });
      qsa('.chp-reveal, .chp-stagger').forEach(el => observer.observe(el));
    });
  } else {
    qsa('.chp-reveal, .chp-stagger').forEach(el => el.classList.add('is-visible'));
  }

  /* ── 5. SMOOTH SCROLL ──────────────────────────────────── */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const target = qs(link.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
  });

  /* ── 6. HERO PARALLAX (desktop only) ───────────────────── */
  const heroBg = qs('.chp-hero__bg img');
  if (heroBg && window.matchMedia('(min-width: 769px)').matches) {
    window.addEventListener('scroll', () => { heroBg.style.transform = `translateY(${window.scrollY * 0.25}px)`; }, { passive: true });
  }

})();